import { Dispatch, MutableRefObject, SetStateAction, useMemo, useRef, useState } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';

import { ActionText } from '@components/action-text';
import { Avatar } from '@components/avatar';
import { Badge } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useDeleteModal } from '@components/generic-modals';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEdit } from '@components/icons/edit';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconList } from '@components/icons/list';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPerson } from '@components/icons/person';
import { IconTrash } from '@components/icons/trash';
import { IconTraveller } from '@components/icons/traveller';
import { IconTravellerCreate } from '@components/icons/traveller-create';
import { IconUpload } from '@components/icons/upload';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PopoverSelect } from '@components/popover-select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Table, TableProps, TableView } from '@components/table';
import { SelectionBanner } from '@components/table/selection-banner';
import { useTableRef } from '@components/table/table';
import { useTableStore } from '@components/table/table-store';
import { ViewsProvider } from '@components/table/toolbar/helpers';
import { AvailableColumn } from '@components/table/types';
import { useViewsManager } from '@components/table/use-views-manager';
import { Text } from '@components/text';
import { useEvent } from '@hooks/use-event';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import {
  useCompanyTravellerAttributeTemplates,
  useCreateTravellerAttributeTemplate,
} from 'api/attributes/hooks';
import {
  TravellerAttributeTemplate,
  TravellerAttributeTemplateData,
} from 'api/attributes/requests';
import {
  useBulkDeleteCompanyTraveller,
  useCompanyTravellersFull,
  useDeleteCompanyTraveller,
  useEditCompanyTraveller,
} from 'api/company-traveller/hooks';
import { CompanyTraveller } from 'api/company-traveller/requests';
import {
  useCachedFavorites,
  useAddFavorite,
  useRemoveFavorite,
  useInvalidateFavorites,
} from 'api/favorites/hooks';
import { CompanyUserFavorite } from 'api/favorites/requests';
import { useJourneys } from 'api/journeys/hooks';
import {
  tableViewToConfig,
  useCreateTableView,
  useDeleteTableView,
  useTableViewSettings,
  useUpdateTableView,
  useUpdateTableViewPreferences,
} from 'api/table-views/hooks';
import { useViewIdUrlSync } from 'api/table-views/use-view-id-url-sync';
import { useUsers } from 'api/users/hooks';
import { TableNameCellContainer } from 'components/table-name-cell-container';
import { TableUserCell } from 'components/table-user-cell';
import { PermissionGuard } from 'contexts/permissions-context';
import { AttributeActions } from 'ui/authenticated/settings/pages/attributes/common';
import { useFieldFormSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/field-form';

import { TravellerAttributes } from './attributes-list';
import { useBulkCompanyTravellerToJourneySidebar } from './sidebars/add-to-journey';
import { useCompanyTravellerSidebar } from './sidebars/form';
import { useCompanyTravellerProfileSidebar } from './sidebars/profile';
import { useFilterConfig } from './use-filter-config';

import { useBulkImportCompanyTravellersModal } from '../bulk-import/company-travellers';

export const CompanyTravellerList = () => {
  const { t } = useScopedTranslate(locale);
  const [selectedTravellers, setSelectedTravellers] = useState<Array<CompanyTraveller>>([]);
  const [searchStr, setSearchStr] = useState('');
  const showCreateSidebar = useCompanyTravellerSidebar();
  const showBulkCreateModal = useBulkImportCompanyTravellersModal();
  const showAddAttributeSidebar = useFieldFormSidebar();
  const createAttributeMutation = useCreateTravellerAttributeTemplate();
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const section = useMatch('/travellers/:section')?.params.section || 'list';
  const tableRef = useTableRef();

  if (companyTravellerAttributeTemplatesQuery.isLoading) {
    return <Spinner />;
  }

  const companyTravellerAttributes = companyTravellerAttributeTemplatesQuery.data ?? [];

  const listActions = (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={setSearchStr} defaultValue={searchStr} />
      <Menu
        buttonProps={{
          variant: 'primary',
          iconLeft: <IconTravellerCreate />,
          text: t('createTraveller'),
          iconRight: <IconDropdown />,
        }}
      >
        <Menu.Item
          label={t('createNewTraveller')}
          onSelect={() => showCreateSidebar()}
          icon={<IconTravellerCreate />}
        />
        <Menu.Item
          label={t('importTravellers')}
          onSelect={showBulkCreateModal}
          icon={<IconUpload />}
        />
      </Menu>
    </Stack>
  );
  return (
    <RouterTabsHeader
      selectionHeader={
        <SelectionHeader
          selectedTravellers={selectedTravellers}
          clearSelectedTravellers={() => {
            setSelectedTravellers([]);
            tableRef.current?.clearSelected();
          }}
        />
      }
      showSelectionHeader={selectedTravellers.length > 0}
      actions={
        section === 'list' ? (
          listActions
        ) : (
          <AttributeActions
            isTravellerAttribute
            setSearchStr={setSearchStr}
            searchStr={searchStr}
            onAddAttribute={() =>
              showAddAttributeSidebar({
                isTravellerAttribute: true,
                onCreate: (data: TravellerAttributeTemplateData) =>
                  createAttributeMutation.mutateAsync(data),
              })
            }
          />
        )
      }
      tabs={[
        {
          content: (
            <TravellerList
              searchStr={searchStr}
              attributes={companyTravellerAttributes ?? []}
              selectedTravellers={selectedTravellers}
              setSelectedTravellers={setSelectedTravellers}
              tableRef={tableRef}
            />
          ),
          label: t('overview'),
          section: 'list',
          icon: <IconList />,
        },
        {
          content: <TravellerAttributes searchStr={searchStr} />,
          label: t('fields'),
          section: 'fields',
          icon: <IconAttributeTraveller />,
        },
      ]}
      breadcrumbs={<Breadcrumb items={[{ label: t('travellers') }]} />}
    />
  );
};

const TravellerOptionsMenu = ({ row: traveller }: { row: CompanyTraveller }) => {
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useCompanyTravellerSidebar();
  const showDeleteModal = useDeleteModal();
  const deleteMutation = useDeleteCompanyTraveller();
  return (
    <Menu
      trigger={IconButton}
      triggerProps={{ variant: 'ghost-purple', size: 'md', icon: <IconMoreVertical /> }}
    >
      <Menu.Item
        label={t('core.edit')}
        icon={<IconEdit />}
        onSelect={() => {
          showEditSidebar({
            companyTravellerShortId: traveller.shortId,
          });
        }}
      />
      <Menu.Item
        label={t('core.delete')}
        icon={<IconTrash />}
        variant="danger"
        onSelect={() => {
          showDeleteModal({
            title: t('deleteTraveller'),
            description: t('deleteTravellerDescription'),
            hasUserConfirmation: true,
            onConfirm: () => {
              deleteMutation.mutateAsync(traveller.shortId);
            },
          });
        }}
      />
    </Menu>
  );
};

const queryParams = { tableType: 'company-travellers' as const };

const TravellerList = ({
  searchStr,
  attributes,
  setSelectedTravellers,
  tableRef,
}: Pick<TableProps, 'tableRef'> & {
  searchStr: string;
  attributes: TravellerAttributeTemplate[];
  selectedTravellers: CompanyTraveller[];
  setSelectedTravellers: Dispatch<SetStateAction<CompanyTraveller[]>>;
}) => {
  const { t } = useScopedTranslate(locale);
  const [searchParams] = useSearchParams();
  const viewIdFromUrl = searchParams.get('viewId');
  const availableColumns = useCompanyTravellerColumns(attributes);
  const { config } = useFilterConfig(attributes);

  const endFixedColumns: TableProps<CompanyTraveller>['endFixedColumns'] = useMemo(() => {
    return [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: TravellerOptionsMenu,
      },
    ];
  }, []);

  const companyTravellersQuery = useCompanyTravellersFull();

  const {
    views: loadedViews,
    selectedViewId,
    isLoading: viewsLoading,
  } = useTableViewSettings(queryParams);
  const createViewMutation = useCreateTableView(queryParams);
  const updateViewMutation = useUpdateTableView(queryParams);
  const deleteViewMutation = useDeleteTableView(queryParams);
  const updatePreferencesMutation = useUpdateTableViewPreferences(queryParams);
  const { invalidateFavorites } = useInvalidateFavorites();

  const favorites = useCachedFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const initialView = useMemo(() => {
    if (!loadedViews?.length) return undefined;
    if (viewIdFromUrl) {
      const viewFromUrl = loadedViews.find((view) => view.id === viewIdFromUrl);
      if (viewFromUrl) return viewFromUrl;
    }
    return loadedViews.find((view) => view.id === selectedViewId) ?? loadedViews[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedViews === undefined]);

  const tableStore = useTableStore({ selectedView: initialView });

  const onSaveView = useEvent((viewId: string, viewConfig: Record<string, any>) => {
    updateViewMutation.mutate({ shortId: viewId, data: { config: viewConfig } });
  });

  const onCreateView = useEvent(
    (view: { name: string; isCollaborative?: boolean; config: Record<string, any> }) => {
      createViewMutation.mutate({
        name: view.name,
        isCollaborative: view.isCollaborative,
        config: view.config,
        tableType: 'company-travellers',
      });
    }
  );

  const onUpdateView = useEvent((view: TableView) => {
    if (!view.id) return;
    const viewConfig = tableViewToConfig(view);
    updateViewMutation.mutate(
      {
        shortId: view.id,
        data: { name: view.name, isCollaborative: view.isCollaborative, config: viewConfig },
      },
      { onSuccess: invalidateFavorites }
    );
  });

  const onDeleteView = useEvent((viewId: string) => {
    deleteViewMutation.mutate(viewId, { onSuccess: invalidateFavorites });
  });

  const onSelectView = useEvent((viewId: string) => {
    updatePreferencesMutation.mutate({ selectedView: viewId });
  });

  const onReorderViews = useEvent((newOrder: string[]) => {
    updatePreferencesMutation.mutate({ viewOrder: newOrder });
  });

  const {
    views: rawViews,
    selectedView: rawSelectedView,
    hasUnsavedChanges,
    createView,
    updateView,
    deleteView,
    selectView,
    reorderViews,
    saveView,
    discardChanges,
  } = useViewsManager({
    views: loadedViews,
    selectedViewId,
    tableStore,
    filterConfig: config,
    onSaveView,
    onCreateView,
    onUpdateView,
    onDeleteView,
    onSelectView,
    onReorderViews,
  });

  const views = useMemo(() => {
    return rawViews.map((view) => ({
      ...view,
      favoriteItem: view.id
        ? favorites.find(
            (favorite) =>
              favorite.contentType === 'companytravellerview' && favorite.objectShortId === view.id
          )
        : undefined,
    }));
  }, [rawViews, favorites]);

  const selectedView = useMemo(() => {
    if (!rawSelectedView) return undefined;
    return views.find((view) => view.id === rawSelectedView.id);
  }, [rawSelectedView, views]);

  useViewIdUrlSync({ selectedViewId: selectedView?.id, views, selectView });

  const onToggleFavorite = useEvent((view: TableView<CompanyUserFavorite>) => {
    const viewId = view.id ?? '';
    if (view.favoriteItem) {
      removeFavorite.mutate(view.favoriteItem.shortId);
    } else {
      addFavorite.mutate({
        contentType: 'companytravellerview',
        objectShortId: viewId,
      });
    }
  });

  if (companyTravellersQuery.isLoading || viewsLoading) {
    return <Spinner />;
  }

  const travellers = companyTravellersQuery.data ?? [];

  const parsedTravellers = travellers.map((traveller) => {
    const attributeArray = traveller.companyTravellerAttributes;
    const dynamicValues = attributes.reduce((acc, attribute) => {
      const attributeId = `attribute-${attribute.shortId}`;

      const travellerAttribute = attributeArray.find(
        (journeyAttribute) => journeyAttribute.attributeShortId === attribute.shortId
      );

      if (attribute.attributeType === 'multi_select') {
        acc[attributeId] = travellerAttribute?.multiOptionValues;
        return acc;
      }

      let attributeValue = travellerAttribute?.value;
      if (!attributeValue) {
        const files = travellerAttribute?.files;
        if (files?.length) {
          attributeValue = files;
        }
      }

      acc[attributeId] = attributeValue;

      return acc;
    }, {} as Record<string, any>);

    return {
      ...traveller,
      ...dynamicValues,
    };
  });

  return (
    <>
      {companyTravellersQuery.isFetching && <Spinner />}
      <ViewsProvider
        views={views}
        selectedView={selectedView}
        createView={createView}
        updateView={updateView}
        deleteView={deleteView}
        selectView={selectView}
        reorderViews={reorderViews}
        onToggleFavorite={onToggleFavorite}
        hasUnsavedChanges={hasUnsavedChanges}
        saveView={saveView}
        discardChanges={discardChanges}
      >
        <Table<CompanyTraveller>
          idColumn="shortId"
          searchStr={searchStr}
          filterProps={{
            addFilterText: 'Filter',
            config,
          }}
          tableRef={tableRef}
          availableColumns={availableColumns}
          data={parsedTravellers}
          endFixedColumns={endFixedColumns}
          onSelectChange={setSelectedTravellers}
          store={tableStore}
          hasViews
          emptyState={
            <EmptyState
              icon={<IconTraveller />}
              header={t('noTravellers')}
              content={t('noTravellersDescription')}
            />
          }
        />
      </ViewsProvider>
    </>
  );
};

const NameCell = ({
  row: traveller,
  selectedIdRef,
  attributes,
}: {
  row: CompanyTraveller;
  selectedIdRef: MutableRefObject<string | undefined>;
  attributes: Array<TravellerAttributeTemplate>;
}) => {
  const showProfileSidebar = useCompanyTravellerProfileSidebar();

  return (
    <TableNameCellContainer>
      <Stack space={2} orientation="horizontal" align="center">
        <Avatar name={traveller.name} src={traveller.picture} />
        <ActionText
          font="regular14"
          color="textPrimary"
          onClick={() => {
            const prevSelectedTraveller = selectedIdRef.current;
            selectedIdRef.current = traveller.shortId;
            return showProfileSidebar({
              companyTravellerShortId: traveller.shortId,
              shouldAnimate: prevSelectedTraveller !== traveller.shortId,
            });
          }}
        >
          {traveller.name}
        </ActionText>
      </Stack>
      <TravellerOptionsMenu row={traveller} />
    </TableNameCellContainer>
  );
};

function useCompanyTravellerColumns(attributes: TravellerAttributeTemplate[]) {
  const selectedTravellerRef = useRef<string>();
  const showProfileSidebar = useCompanyTravellerProfileSidebar();
  const usersQuery = useUsers();
  const editCompanyTraveller = useEditCompanyTraveller();

  const dynamicColumns = useMemo(() => {
    return attributes.map((attribute) => ({
      title: attribute.templateTitle,
      type: attribute.attributeType,
      id: `attribute-${attribute.shortId}`,
      onMultiFilesClick: (traveller) => {
        showProfileSidebar({
          companyTravellerShortId: traveller.shortId,
        });
      },
      Cell:
        attribute.attributeType === 'user'
          ? ({ row }) => (
              <TableUserCell row={row} attribute={attribute} users={usersQuery.data ?? []} />
            )
          : undefined,
      defaultHidden: true,
      onEdit: ['text', 'paragraph'].includes(attribute.attributeType)
        ? async (row, value: string) => {
            const formData = new FormData();
            formData.append(`company_traveller_attribute__${attribute.shortId}`, value);
            await editCompanyTraveller.mutateAsync({
              shortId: row.shortId,
              data: formData,
            });
          }
        : undefined,
    })) satisfies Array<AvailableColumn<CompanyTraveller>>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, usersQuery.data]);

  return useMemo(() => {
    const { t } = getScopedTranslate(locale);
    return [
      {
        title: t('core.fullName'),
        type: 'text',
        renderHeaderIcon: () => <IconPerson size={16} />,
        id: 'name',
        Cell: ({ row }) => (
          <NameCell attributes={attributes} row={row} selectedIdRef={selectedTravellerRef} />
        ),
      },
      {
        id: 'email',
        title: t('core.email'),
        type: 'email',
      },
      {
        id: 'phone',
        title: t('core.phone'),
        type: 'phone_number',
        renderFallback: (traveller) => (
          <AttributeFallback traveller={traveller} attributes={attributes} />
        ),
      },
      {
        title: t('numJourneys'),
        renderHeaderIcon: () => <IconJourneyItem size={16} />,
        id: 'numJourneys',
        type: 'number',
      },
      {
        id: 'dateUpdated',
        title: t('dateModified'),
        type: 'date',
      },
      {
        id: 'dateCreated',
        title: t('dateCreated'),
        type: 'date',
      },
      ...dynamicColumns,
    ] satisfies Array<AvailableColumn<CompanyTraveller>>;
  }, [attributes, dynamicColumns]);
}

const AttributeFallback = ({
  traveller,
  attributes,
}: {
  traveller: CompanyTraveller;
  attributes: TravellerAttributeTemplate[];
}) => {
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useCompanyTravellerSidebar();
  return (
    <ActionText
      onClick={() =>
        showEditSidebar({
          companyTravellerShortId: traveller.shortId,
          companyTravellerAttributes: attributes,
        })
      }
      color="textHelper"
      font="regular14"
    >
      {t('core.not_set')}
    </ActionText>
  );
};

const SelectionHeader = ({
  selectedTravellers,
  clearSelectedTravellers,
}: {
  selectedTravellers: CompanyTraveller[];
  clearSelectedTravellers(): void;
}) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const showAddSidebar = useBulkCompanyTravellerToJourneySidebar();
  const bulkDeleteMutation = useBulkDeleteCompanyTraveller({
    onSuccess: clearSelectedTravellers,
  });

  const journeysQuery = useJourneys('?ordering=title');

  const options = (journeysQuery.data ?? [])
    .filter((journey) => journey.hasPublishedJourney)
    .map((journey) => ({
      value: journey.shortId,
      label: journey.title,
      helpText: journey.description || t('noDescription'),
    }));

  return (
    <SelectionBanner
      selectedCount={selectedTravellers.length}
      clearSelected={clearSelectedTravellers}
    >
      <PermissionGuard permission="canViewTravellerProfiles">
        <PopoverSelect
          trigger={
            <Button
              variant="ghost"
              text={t('addToJourney')}
              dataTestid="add-to-journey-button-topbar"
              iconLeft={<IconJourneyItem />}
            />
          }
          width={pxToRem(400)}
          maxHeight={464}
          items={options}
          searchPlaceholder={`${t('addToJourney')}...`}
          clearSelected
          onChange={(selectedOptions) => {
            selectedOptions.length > 0 &&
              showAddSidebar({
                journeyShortId: selectedOptions[0],
                selectedTravellers: selectedTravellers.map((traveller) => ({
                  companyTravellerShortId: traveller.shortId,
                })),
              });
          }}
          placement="bottom-start"
        />
        <Separator orientation="horizontal" />
      </PermissionGuard>
      <IconButton
        icon={<IconTrash />}
        variant="ghost-purple"
        tooltip={t('deleteTravellers')}
        dataTestid="delete-travellers-icon-button"
        onClick={() =>
          showDeleteModal({
            title: t('bulkDeletTitle'),
            description: t('bulkDeleteDescription'),
            hasUserConfirmation: true,
            buttonText: t('core.delete'),
            buttonIcon: <IconTrash />,
            onConfirm: async () => {
              await bulkDeleteMutation.mutateAsync({
                ids: selectedTravellers.map((traveller) => traveller.shortId),
                selectAll: false,
              });
            },
            children: (
              <Stack orientation="horizontal" justify="space-between">
                <Text font="bold16" text={t('travellersSelected')} />
                <Badge text={selectedTravellers.length} size="md" />
              </Stack>
            ),
          })
        }
      />
    </SelectionBanner>
  );
};

const locale = {
  en: {
    travellers: 'Traveller profiles',
    createTraveller: 'Create traveller',
    createNewTraveller: 'Create new traveller',
    dateCreated: 'Created date',
    dateModified: 'Last modified',
    addAttribute: 'Add',
    showing: 'Showing {results} / {total} travellers',
    noTravellers: 'No travellers',
    noTravellersDescription:
      'To start sending your travellers on journeys, create your first traveller here',
    deleteTraveller: 'Delete traveller',
    deleteTravellerDescription:
      'Are you sure you want to delete this traveller along with all of its associated history and journey instances?',
    importTravellers: 'Import travellers • CSV',
    deleteTravellers: 'Delete travellers',
    numJourneys: '# Journeys',
    pageSelected: 'All {count} Travellers on page selected',
    selectAllTravellers: 'Select all {count}',
    allSelected: 'All {count} Travellers selected',
    clearSelection: 'Clear selection',
    bulkDeletTitle: 'Delete traveller/s',
    bulkDeleteDescription:
      'Are you sure you want to delete the selected travellers? They will be removed from the system and all journey instances and progress will be lost',
    travellersSelected: 'Traveller/s Selected',
    addToJourney: 'Add to journey',
    noDescription: 'No description',
    overview: 'Overview',
    fields: 'Fields',

    copyPhone: 'Copy phone',
    copyEmail: 'Copy email',
  },
};
